// src/PublicUser/pages/ToolPage.jsx
import React, { useState, useMemo } from 'react';
import { AI_TOOLS_DATA, CATEGORIES } from '../data/FreeTools';
import ToolsHero from '../components/tools/ToolsHero';
import CategoryFilter from '../components/tools/CategoryFilter';
import ToolsGrid from '../components/tools/ToolsGrid';
import '../css/ToolsPage.css';

export default function ToolPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [favorites, setFavorites] = useState([]);
  const [showFavsOnly, setShowFavsOnly] = useState(false);

  // Compute stats across global array matrix
  const stats = useMemo(() => {
    return {
      total: AI_TOOLS_DATA.length,
      free: AI_TOOLS_DATA.filter(t => t.pricing.toLowerCase() === 'free').length,
      freemium: AI_TOOLS_DATA.filter(t => t.pricing.toLowerCase() === 'freemium').length,
      paid: AI_TOOLS_DATA.filter(t => t.pricing.toLowerCase() === 'paid').length,
    };
  }, []);

  const handleToggleSave = (id) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(favId => favId !== id) : [...prev, id]
    );
  };

  // Primary filtration sequence match
  const filteredTools = useMemo(() => {
    return AI_TOOLS_DATA.filter((tool) => {
      const matchesCategory = activeCategory === 'All' || tool.category === activeCategory;
      const matchesSearch = tool.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            tool.description.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFav = !showFavsOnly || favorites.includes(tool.id);
      
      return matchesCategory && matchesSearch && matchesFav;
    });
  }, [searchQuery, activeCategory, showFavsOnly, favorites]);

  // Isolate high priority premium items globally for top banner placement
  const featuredTools = useMemo(() => {
    return filteredTools.filter(tool => tool.featured);
  }, [filteredTools]);

  // Dynamic heading taxonomy grouping dictionary structure mapping
  const categorizedGroups = useMemo(() => {
    if (activeCategory !== 'All') return [];
    
    // Determine which categories actually hold items matching current search filters
    const groups = {};
    filteredTools.forEach((tool) => {
      if (!groups[tool.category]) {
        groups[tool.category] = [];
      }
      groups[tool.category].push(tool);
    });
    
    // Return array layout ordering matching the master CATEGORIES array configuration
    return CATEGORIES.filter(cat => cat !== 'All' && groups[cat]?.length > 0).map(cat => ({
      categoryName: cat,
      tools: groups[cat]
    }));
  }, [filteredTools, activeCategory]);

  return (
    <div className="ts-tools-wrapper">
      <ToolsHero 
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        stats={stats}
        showFavsOnly={showFavsOnly}
        setShowFavsOnly={setShowFavsOnly}
        favCount={favorites.length}
      />

<div className="ts-tools-filter-section-warpper">
      <CategoryFilter 
        activeCategory={activeCategory}
        setActiveCategory={setActiveCategory}
      />
</div>
      <div className="ts-tools-content">
        {filteredTools.length > 0 ? (
          <>
            {/* Render global featured highlights bar component at top if active */}
            {/* {featuredTools.length > 0 && (
              <ToolsGrid 
                title="✨ Featured Highlights" 
                tools={featuredTools} 
                favorites={favorites}
                onToggleSave={handleToggleSave}
              />
            )} */}

            {/* Layout decision based on active heading selections */}
            {activeCategory === 'All' ? (
              categorizedGroups.map(group => (
                <ToolsGrid 
                  key={group.categoryName}
                  title={`📂 ${group.categoryName} Tools`}
                  tools={group.tools}
                  favorites={favorites}
                  onToggleSave={handleToggleSave}
                />
              ))
            ) : (
              <ToolsGrid 
                title={`📂 ${activeCategory} Toolkit`}
                tools={filteredTools}
                favorites={favorites}
                onToggleSave={handleToggleSave}
              />
            )}
          </>
        ) : (
          <div className="ts-tools-empty-state">
            <h4>No software setups found</h4>
            <p>Your adjustments did not return corresponding system matches.</p>
          </div>
        )}
      </div>
    </div>
  );
}